import { FC, useContext, useMemo, useState } from 'react';

import { gql, useApolloClient, useQuery } from '@apollo/client';
import { Button, H2, Modal, Search, Separator } from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { ClientSuggest } from 'components';
import { LocalizationContext } from 'localization';

import { IChosenClientItem } from '../interfaces';
import s from './AddClientModal.module.scss';
import { IAddClientModalProps } from './interfaces';
import { GET_USERS_GROUPS } from './schema';

export const AddClientModal: FC<IAddClientModalProps> = ({
  onClose,
  chosenItems,
  setChosenItems,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const [closeDropDown, setCloseDropDown] = useState<boolean>(false);
  const [clientUUID, setClientUUID] = useState<string | null>(null);
  const [tempItems, setTempItems] = useState<IChosenClientItem[]>(chosenItems);

  const { data: userGroups } = useQuery(GET_USERS_GROUPS, {
    fetchPolicy: 'cache-first',
  });
  const apolloClient = useApolloClient();

  const OPTIONS = useMemo(() => {
    return (
      userGroups?.userGroups
        ?.filter(
          (service: { title: string; uuid: string }) =>
            !tempItems.find(
              (item) => item.id === service?.uuid && item.type === 'group'
            )
        )
        .map((group: { title: string; uuid: string }) => ({
          value: group?.uuid,
          title: group?.title,
        })) || []
    );
  }, [userGroups, tempItems]);

  const handleAddGroup = (groupId: string | null): void => {
    if (groupId === null) return;

    const selectedGroup = OPTIONS.find(
      (option: { title: string; value: string }) => option.value === groupId
    );
    if (!selectedGroup) return;

    const newGroup: IChosenClientItem = {
      id: selectedGroup.value,
      title: selectedGroup.title,
      type: 'group',
    };

    const isDuplicate = tempItems.some(
      (item) => item.id === newGroup.id && item.type === 'group'
    );

    if (!isDuplicate) {
      setTempItems((prev) => [...prev, newGroup]);
      setCloseDropDown(true);
    }
  };

  const handleAddClient = (uuid: string | null): void => {
    if (!uuid) return;

    const clientData = apolloClient.readFragment({
      id: apolloClient.cache.identify({
        __typename: 'User',
        uuid: uuid,
      }),
      fragment: gql`
        fragment ClientPhone on User {
          uuid
          phone
          nickname
        }
      `,
    });

    if (!clientData) return;

    const newClient: IChosenClientItem = {
      id: clientData.uuid,
      title: clientData.phone,
      type: 'client',
    };

    const isDuplicate = tempItems.some(
      (item) => item.id === newClient.id && item.type === 'client'
    );

    if (!isDuplicate) {
      setTempItems((prev) => [...prev, newClient]);
    }
  };

  const handleRemoveItem = (
    id: number | string,
    type: 'client' | 'group'
  ): void => {
    setTempItems((prev) =>
      prev.filter((item) => !(item.id === id && item.type === type))
    );
  };

  const handleSave = (): void => {
    setChosenItems(tempItems);
    onClose();
  };

  const handleCancel = (): void => {
    onClose();
  };

  const hasClients = tempItems.some((item) => item.type === 'client');
  const hasGroups = tempItems.some((item) => item.type === 'group');

  return (
    <Modal className={s.container} onClose={handleCancel}>
      <div className={s.header}>
        <H2>{t('promo.client.modal.title')}</H2>
        <span className={s.text}>{t('promo.client.modal.text')}</span>
      </div>

      <div className={s.tabs_block}>
        <ClientSuggest
          value={null}
          onChange={(value) => {
            if (!permissions?.has('client_info')) {
              clientUUID === '' ? setClientUUID(null) : setClientUUID('');
              return;
            }

            handleAddClient(value);
          }}
          className={s['client-search']}
          disabled={hasGroups || hasClients}
        />
        <Search
          closeDropDown={closeDropDown}
          name={'select'}
          items={OPTIONS}
          value={null}
          onChange={(value) => {
            if (typeof value === 'string') {
              handleAddGroup(value);
            }
          }}
          placeholder={t('promo.client.modal.group')}
          variant="secondary"
          className={s.search}
          onInputChange={() => {
            setCloseDropDown(false);
          }}
          notFoundText={t('goods.notFound')}
          autocomplete="off"
          preventSubmitOnEnter={true}
          disabled={hasClients}
        />
      </div>

      <Separator />

      <div className={cn(s.chosen, tempItems.length && s.active)}>
        {tempItems.length ? (
          <>
            <span className={s.text}>{t('promo.position.chosen')}</span>
            <div className={s.items}>
              {tempItems.map((item) => (
                <div key={`${item.type}-${item.id}`} className={s.item}>
                  <span className={s.item_text}>{item.title}</span>
                  <Button
                    onClick={() => handleRemoveItem(item.id, item.type)}
                    iconName="CloseIcon"
                    onlyIcon
                    className={s.button}
                  />
                </div>
              ))}
            </div>
          </>
        ) : (
          <span className={s.text}>{t('promo.position.modal.chosen')}</span>
        )}
      </div>

      <div className={s.buttons_block}>
        <Button onClick={handleSave} fullWidth disabled={!tempItems.length}>
          {t('add')}
        </Button>
        <Button fullWidth variant="secondary" onClick={handleCancel}>
          {t('cancel')}
        </Button>
      </div>
    </Modal>
  );
};
